// src/components/Dashboard.js
import React, { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../utils/AuthContext';
import { getUserLaps, getUserVehicles, deleteVehicle, deleteLap, getCircuits, updateUserProfile, getUserProfile, getLeaderboard } from '../utils/firestoreUtils';
import { storage, auth } from '../utils/firebase';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Button, Card, CardContent, Typography, Container, TextField, Avatar, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Fab, Menu, MenuItem } from '@mui/material';
import { Add } from '@mui/icons-material';
import AddVehicleModal from './AddVehicleModal';
import AddLapModal from './AddLapModal';

// Reusable styled lap card
function LapCard({ lap, circuits, vehicles, onClick }) {
  const fmt = (time) => {
    if (!time) return '0.000';
    const s = parseFloat(time);
    const m = Math.floor(s / 60), sec = Math.floor(s % 60), ms = Math.round((s % 1) * 1000);
    if (m === 0) return `${sec.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
    return `${m}:${sec.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
  };
  const getTrackName = () => {
    const circuit = circuits.find(c => c.id === lap.track.split('-')[0]);
    return circuit ? circuit.name : 'Unknown';
  };
  const statusColor = lap.isApproved ? 'var(--green)' : lap.hasBeenReviewed ? 'var(--red)' : '#60aaff';
  const statusBg = lap.isApproved ? 'rgba(0,196,113,0.22)' : lap.hasBeenReviewed ? 'rgba(255,59,48,0.22)' : 'rgba(96,170,255,0.22)';
  const statusLabel = lap.isApproved ? '✓ Verified' : lap.hasBeenReviewed ? '✕ Denied' : '○ Pending';
  const bgImage = lap.images?.length > 0 ? lap.images[lap.mainImageIndex || 0] : null;
  const vehName = vehicles.find(v => v.id === lap.vehicleId)?.name || '';
  return (
    <div onClick={onClick} style={{ cursor: 'pointer', borderRadius: 'var(--radius-lg)', overflow: 'hidden', position: 'relative', minHeight: 200, background: bgImage ? `url(${bgImage}) center/cover` : 'var(--text-primary)', boxShadow: 'var(--shadow-sm)', transition: 'transform var(--transition), box-shadow var(--transition)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', border: '1px solid var(--border)' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--shadow-lg)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
    >
      <div style={{ position: 'absolute', inset: 0, background: bgImage ? 'linear-gradient(180deg,rgba(0,0,0,0.6) 0%,rgba(0,0,0,0.0) 45%,rgba(0,0,0,0.0) 55%,rgba(0,0,0,0.75) 100%)' : 'linear-gradient(160deg,rgba(25,130,206,0.1) 0%,transparent 60%)', zIndex: 1 }} />
      {/* Top row: status + points */}
      <div style={{ position: 'relative', zIndex: 2, padding: '12px 12px 0', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: statusBg, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', border: `1px solid ${statusColor}55`, borderRadius: 'var(--radius-pill)', padding: '4px 10px' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: statusColor }}>{statusLabel}</span>
        </div>
        {lap.points > 0 && (
          <div style={{ background: 'rgba(240,165,0,0.22)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', border: '1px solid rgba(240,165,0,0.45)', borderRadius: 'var(--radius-pill)', padding: '4px 10px', fontSize: '0.75rem', fontWeight: 700, color: 'var(--gold)' }}>
            {lap.points} {lap.points === 1 ? 'pt' : 'pts'}
          </div>
        )}
      </div>
      {/* Bottom: time + info */}
      <div style={{ position: 'relative', zIndex: 2, padding: '0 14px 14px' }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.75rem', fontWeight: 700, color: '#fff', lineHeight: 1, marginBottom: 8, textShadow: '0 1px 8px rgba(0,0,0,0.8),0 0 24px rgba(0,0,0,0.5)', letterSpacing: '-0.01em' }}>
          {fmt(lap.bestLap)}
        </div>
        <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#fff', textShadow: '0 1px 4px rgba(0,0,0,0.9)', marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {lap.eventName}
        </div>
        <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.82)', textShadow: '0 1px 3px rgba(0,0,0,0.9)', display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          <span>{getTrackName()}</span>
          {vehName && <><span style={{ opacity: 0.4 }}>·</span><span>{vehName}</span></>}
        </div>
      </div>
    </div>
  );
}


function Dashboard() {
  const { user } = useContext(AuthContext);
  const [vehicles, setVehicles] = useState([]);
  const [allLaps, setAllLaps] = useState([]);
  const [currentSeasonLaps, setCurrentSeasonLaps] = useState([]);
  const [historicalLaps, setHistoricalLaps] = useState([]);
  const [circuits, setCircuits] = useState([]);
  const [openVehicleModal, setOpenVehicleModal] = useState(false);
  const [openLapModal, setOpenLapModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [selectedLap, setSelectedLap] = useState(null);
  const [bio, setBio] = useState('');
  const [headshot, setHeadshot] = useState('');
  const [openHeadshotModal, setOpenHeadshotModal] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userPosition, setUserPosition] = useState(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const circs = await getCircuits();
        setCircuits(circs);
        const veh = await getUserVehicles(user.uid);
        setVehicles(veh);
        const lps = await getUserLaps(user.uid, false); // For own user, fetch all laps
        setAllLaps(lps);

        // Filter laps by current year
        const currentYear = new Date().getFullYear();
        const currentSeason = lps.filter(lap => {
          const lapDate = lap.date?.toDate ? lap.date.toDate() : new Date(lap.date);
          return lapDate.getFullYear() === currentYear;
        });
        const historical = lps.filter(lap => {
          const lapDate = lap.date?.toDate ? lap.date.toDate() : new Date(lap.date);
          return lapDate.getFullYear() < currentYear;
        });
        setCurrentSeasonLaps(currentSeason);
        setHistoricalLaps(historical);
        const profile = await getUserProfile(user.uid);
        if (profile) {
          setBio(profile.bio || '');
          setHeadshot(profile.headshot || '');
        }

        // Fetch leaderboard and calculate user position
        const lb = await getLeaderboard();
        setLeaderboard(lb);
        const userEntry = lb.find(entry => entry.userId === user.uid);
        if (userEntry) {
          const position = lb.findIndex(entry => entry.userId === user.uid) + 1;
          setUserPosition(position);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };
    fetchData();
  }, [user]);

  const handleEditVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setOpenVehicleModal(true);
  };

  const handleDeleteVehicle = async (vehicleId) => {
    try {
      await deleteVehicle(user.uid, vehicleId);
      setVehicles(vehicles.filter(v => v.id !== vehicleId));
    } catch (error) {
      alert(error.message);
    }
  };

  const handleEditLap = (lap) => {
    setSelectedLap(lap);
    setOpenLapModal(true);
  };

  const handleDeleteLap = async (lapId) => {
    try {
      await deleteLap(user.uid, lapId);
      const updatedLaps = allLaps.filter(l => l.id !== lapId);
      setAllLaps(updatedLaps);

      // Update filtered laps
      const currentYear = new Date().getFullYear();
      const currentSeason = updatedLaps.filter(lap => {
        const lapDate = lap.date?.toDate ? lap.date.toDate() : new Date(lap.date);
        return lapDate.getFullYear() === currentYear;
      });
      const historical = updatedLaps.filter(lap => {
        const lapDate = lap.date?.toDate ? lap.date.toDate() : new Date(lap.date);
        return lapDate.getFullYear() < currentYear;
      });
      setCurrentSeasonLaps(currentSeason);
      setHistoricalLaps(historical);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleCloseVehicleModal = async () => {
    setOpenVehicleModal(false);
    setSelectedVehicle(null);
    // Refetch vehicles in case of update
    const veh = await getUserVehicles(user.uid);
    setVehicles(veh);
  };

  const handleCloseLapModal = async () => {
    setOpenLapModal(false);
    setSelectedLap(null);
    // Refetch laps in case of update
    const lps = await getUserLaps(user.uid, false);
    setAllLaps(lps);

    // Update filtered laps
    const currentYear = new Date().getFullYear();
    const currentSeason = lps.filter(lap => {
      const lapDate = lap.date?.toDate ? lap.date.toDate() : new Date(lap.date);
      return lapDate.getFullYear() === currentYear;
    });
    const historical = lps.filter(lap => {
      const lapDate = lap.date?.toDate ? lap.date.toDate() : new Date(lap.date);
      return lapDate.getFullYear() < currentYear;
    });
    setCurrentSeasonLaps(currentSeason);
    setHistoricalLaps(historical);
  };

  const handleAddMenuOpen = (event) => {
    setAddMenuAnchor(event.currentTarget);
  };

  const handleAddMenuClose = () => {
    setAddMenuAnchor(null);
  };

  const handleAddLap = () => {
    setAddMenuAnchor(null);
    setSelectedLap(null);
    setOpenLapModal(true);
  };

  const handleAddVehicle = () => {
    setAddMenuAnchor(null);
    setSelectedVehicle(null);
    setOpenVehicleModal(true);
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      alert('Please enter your current password');
      return;
    }
    if (newPassword !== confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    try {
      // Re-authenticate user with current password
      const credential = EmailAuthProvider.credential(auth.currentUser.email, currentPassword);
      await reauthenticateWithCredential(auth.currentUser, credential);

      // Update to new password
      await updatePassword(auth.currentUser, newPassword);
      alert('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error updating password:', error);
      if (error.code === 'auth/wrong-password') {
        alert('Current password is incorrect');
      } else if (error.code === 'auth/weak-password') {
        alert('New password is too weak');
      } else {
        alert('Error updating password. Please try again.');
      }
    }
  };

  const formatLapTime = (time) => {
    if (!time) return '0.000';
    const totalSeconds = parseFloat(time);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const milliseconds = Math.round((totalSeconds % 1) * 1000);
    if (minutes === 0) {
      return `${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
  };

  const handleSaveProfile = async () => {
    try {
      await updateUserProfile(user.uid, { bio, headshot });
      alert('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile. Please try again.');
    }
  };

  const handleHeadshotUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // Compress and resize image
      const Compressor = (await import('compressorjs')).default;
      new Compressor(file, {
        quality: 0.8,
        maxWidth: 400,
        maxHeight: 400,
        success: async (compressedFile) => {
          // Upload to Firebase Storage
          const storageRef = ref(storage, `users/${user.uid}/headshot.jpg`);
          const uploadTask = uploadBytesResumable(storageRef, compressedFile);

          uploadTask.on('state_changed',
            (snapshot) => {},
            (error) => {
              console.error('Error uploading headshot:', error);
              alert('Error uploading headshot. Please try again.');
            },
            async () => {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              setHeadshot(downloadURL);
              setOpenHeadshotModal(false); // Close modal after successful upload
            }
          );
        },
        error: (error) => {
          console.error('Error compressing headshot:', error);
          alert('Error processing headshot. Please try again.');
        }
      });
    } catch (error) {
      console.error('Error uploading headshot:', error);
      alert('Error uploading headshot. Please try again.');
    }
  };

  return (
    <Container maxWidth="xl">
      <h1 className="page-title" style={{ marginBottom: 24 }}>Your Profile</h1>
      <Grid container spacing={3} style={{ marginTop: '16px', marginBottom: '32px', display: 'flex', alignItems: 'stretch' }}>
        <Grid item xs={12} md={8} style={{ display: 'flex' }}>
          <Card className="card" style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent style={{ paddingBottom: '8px', flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <Avatar
                  src={headshot}
                  alt="Headshot"
                  sx={{ width: 80, height: 80, marginRight: '16px', cursor: 'pointer' }}
                  onClick={() => setOpenHeadshotModal(true)}
                />
                <div>
                  <Typography variant="h5">{user.displayName}</Typography>
                </div>
              </div>
              <TextField
                label="Bio"
                multiline
                rows={3}
                fullWidth
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                placeholder="Tell us about yourself..."
                style={{ marginBottom: '16px' }}
              />
              <Button onClick={handleSaveProfile} variant="contained">Save Profile</Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4} style={{ display: 'flex' }}>
          <Card className="card" style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent style={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="h6" style={{ fontFamily: 'Oxygen, sans-serif', fontWeight: 200, marginBottom: '16px' }}>
                Championship Standing
              </Typography>
              <Typography variant="h3" className="stat-number" style={{ marginBottom: '8px' }}>
                {userPosition ? `#${userPosition}` : '-'}
              </Typography>
              <Typography variant="body1" style={{ color: '#666', marginBottom: '16px' }}>
                Position
              </Typography>
              <Typography variant="h4" className="stat-number">
                {leaderboard.find(entry => entry.userId === user.uid)?.points || 0}
              </Typography>
              <Typography variant="body1" style={{ color: '#666' }}>
                Total Points
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h4" className="section-heading" style={{}}>Your Vehicles</Typography>
      <div className="card-grid">
        {vehicles.map((veh) => (
          <Card
            key={veh.id}
            className="card"
            onClick={() => handleEditVehicle(veh)}
            style={{
              cursor: 'pointer',
              backgroundImage: veh.images && veh.images.length > 0 ? `url(${veh.images[veh.mainImageIndex || 0]})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              position: 'relative'
            }}
          >
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.7))',
              zIndex: 1
            }} />
            <CardContent style={{ position: 'relative', zIndex: 2, color: 'white' }}>
              <Typography variant="h6">{veh.name}</Typography>
              {veh.model && (
                <Typography variant="body2" style={{ opacity: 0.9 }}>
                  {veh.model} {veh.year && `(${veh.year})`}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
      <AddVehicleModal open={openVehicleModal} onClose={handleCloseVehicleModal} userId={user.uid} vehicle={selectedVehicle} onDelete={handleDeleteVehicle} />

      <Typography variant="h4" className="section-heading" style={{}}>This Season's Laps</Typography>
      <div className="card-grid">
        {currentSeasonLaps.length === 0 ? (
          <div className="empty-lap-card" onClick={handleAddLap}>
            <div className="empty-lap-card-icon">🏁</div>
            <div className="empty-lap-card-label">No Laps Yet</div>
            <div className="empty-lap-card-sub">Tap to add your first lap this season</div>
          </div>
        ) : (
          currentSeasonLaps.map((lap) => (
            <LapCard key={lap.id} lap={lap} circuits={circuits} vehicles={vehicles} onClick={() => handleEditLap(lap)} />
          ))
        )}
      </div>
      <AddLapModal open={openLapModal} onClose={handleCloseLapModal} userId={user.uid} lap={selectedLap} onDelete={handleDeleteLap} />

      {historicalLaps.length > 0 && (
        <>
          <Typography variant="h4" className="section-heading" style={{}}>Historical Laps</Typography>
          <div className="card-grid">
            {historicalLaps.map((lap) => (
              <LapCard key={lap.id} lap={lap} circuits={circuits} vehicles={vehicles} onClick={() => handleEditLap(lap)} />
            ))}
          </div>
        </>
      )}

      {/* Password Change Section */}
      <Typography variant="h4" className="section-heading" style={{}}>Change Password</Typography>
      <Card className="card" style={{ padding: '24px', marginTop: '16px' }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={3}>
            <TextField
              label="Current Password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
              sx={{ mt: 0 }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              sx={{ mt: 0 }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              sx={{ mt: 0 }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              onClick={handleChangePassword}
              fullWidth
              sx={{ height: '56px', mt: 0 }}
            >
              Update Password
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Headshot Modal */}
      <Dialog open={openHeadshotModal} onClose={() => setOpenHeadshotModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Headshot</DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: '16px', color: '#666' }}>
            Select an image of yourself, not your car!
          </Typography>
          <input
            type="file"
            accept="image/*"
            onChange={handleHeadshotUpload}
            style={{ marginBottom: '16px' }}
          />
          {headshot && (
            <div style={{ textAlign: 'center' }}>
              <Typography variant="body2" style={{ marginBottom: '8px' }}>Preview:</Typography>
              <Avatar
                src={headshot}
                alt="Headshot Preview"
                sx={{ width: 120, height: 120, margin: '0 auto' }}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHeadshotModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={handleAddMenuOpen}
      >
        <Add />
      </Fab>

      {/* Add Menu */}
      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={handleAddMenuClose}
      >
        <MenuItem onClick={handleAddLap}>Add Lap</MenuItem>
        <MenuItem onClick={handleAddVehicle}>Add Vehicle</MenuItem>
      </Menu>
    </Container>
  );
}

export default Dashboard;